/**
 * PURE. Head recipes (creatures §B7.2; art §5.1, §5.5 "solemn derp"): skull loft with the variant profiles, muzzle /
 * snout variants with a jaw-skinned chin (the mouth opens on a seam), sclera-iris-pupil discs, the lid shell concentric
 * with the lid bone (base expression baked), 2-facet ink brows, nose, the ink mouth line skinned head↔mouth (corners 100 %
 * mouth), teeth, whiskers, ears (extruded profiles, inner ear flesh), horns, hair, beards. Raw units (see skeleton.ts).
 */
import type { BoneName } from '../../shared';
import { gazeOffset, lidAngle, type EyeGeom } from '../rig/face';
import { flatten } from '../rig/skull';
import type { Surface } from './builder';
import { MAT, REG, part, type BuildCtx } from './ctx';
import {
  add, blend, cross, ellipse, extrudeProfile, fan, loft, nrm, resample, rigid, scl, strip, sub, type Ring, type Skin, type V3,
} from './prims';

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;

// ─────────────────────────────────────────────────────────────────────────────────────────────── skull ──
function skull(ctx: BuildCtx): void {
  const { rig, cfg } = ctx;
  const prof = rig.skull;
  const head = ctx.bi('head');
  part(ctx, 'head.skull', (s) => {
    const n = cfg.headRings;
    const rings: Ring[] = [];
    for (let i = 0; i < n; i++) {
      const f = 0.93 * Math.pow(i / (n - 1), 0.92);
      const r = prof.ring(f);
      rings.push({
        c: [0, r.y, r.cz], rs: r.rs, rf: r.rf, skin: rigid(head), lw: f < 0.12 ? 1.4 : 1,
        shape: (th) => prof.shape(f, th),
        deform: prof.facePlane !== null ? (p) => [p[0], p[1], flatten(p[2], f, prof.facePlane!)] : undefined,
      });
    }
    const top = rings[n - 1].c[1];
    loft(s, rings, {
      sides: cfg.headSides, front: [0, 0, 1], stagger: true, mat: ctx.headMat, region: REG.skull, capStart: 'flat', capEnd: 'point',
      capEndLen: ctx.d.crown + prof.crownLift * ctx.d.hH - top, jitter: 0.025, seed: ctx.seed, tangent: () => [0, 1, 0],
    });
  });
  ctx.skullFront = (x, y) => prof.front(x, y);
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── muzzle ──
/** Half-ellipsoid patch along +Z (rings in x/y) clamped to one side of the mouth plane. */
function ellipsoidZ(s: Surface, c: V3, rx: number, ry: number, rz: number, sides: number, rings: number, skin: Skin | ((p: V3) => Skin), o: {
  mat: number; region: number; clampMin?: number; clampMax?: number; lw?: number; back?: number; taper?: number; bendY?: number;
}): void {
  const rs: Ring[] = [];
  const zb = c[2] - rz * (o.back ?? 0.6);
  for (let i = 0; i < rings; i++) {
    const t = i / (rings - 1);
    const z = zb + (c[2] + rz * 0.82 - zb) * t;
    const e = Math.max(0.12, Math.sqrt(Math.max(0, 1 - Math.pow((z - c[2]) / rz, 2))));
    const tap = 1 - (o.taper ?? 0) * t;
    const yOff = (o.bendY ?? 0) * t * t;
    rs.push({
      c: [c[0], c[1] + yOff, z], rs: rx * e * tap, rf: ry * e * tap, skin: typeof skin === 'function' ? rigid(0) : skin, lw: o.lw ?? 1,
      deform: (p) => {
        let y = p[1];
        if (o.clampMin !== undefined && y < o.clampMin) y = o.clampMin;
        if (o.clampMax !== undefined && y > o.clampMax) y = o.clampMax;
        return [p[0], y, p[2]];
      },
      skinAt: typeof skin === 'function' ? (_th, p) => skin(p) : undefined,
    });
  }
  loft(s, rs, { sides, front: [0, 1, 0], mat: o.mat, region: o.region, capEnd: 'point', capEndLen: rz * 0.18, capStart: 'none',
    tangent: () => [0, 0, 1] });
}

interface MuzzleInfo { front: (x: number, y: number) => number; noseAt: V3; seam: (t: number) => V3; halfW: number }

function muzzle(ctx: BuildCtx): void {
  const { spec, d, cfg } = ctx;
  const head = ctx.bi('head'), jaw = ctx.bi('jaw');
  const snout = spec.o.snout;
  const m = spec.v.muzzle;
  const sw = spec.v.snoutW;
  const my = d.mouthY;
  const front0 = ctx.skullFront!;
  const fz = (x: number, y: number): number => front0(x, y);
  const sides = cfg.muzzleSides, rings = cfg.muzzleRings + 1;
  const under = spec.v.underbite * 0.008;
  let info: MuzzleInfo;
  const mat = ctx.headMat;
  if (snout === 'dog' || snout === 'pointed' || snout === 'goat') {
    const len = 0.03 + m * 1.05 + (snout === 'goat' ? 0.02 : 0);
    const rx = 0.05 * sw * d.sx * (snout === 'pointed' ? 0.85 : 1), ry = 0.036 * d.sy;
    const z0 = fz(0, my + 0.02) - 0.03;
    const c: V3 = [0, my + ry * 0.55, z0 + len / 2];
    const taper = snout === 'pointed' ? 0.55 : snout === 'goat' ? 0.35 : 0.28;
    part(ctx, 'head.muzzle', (s) => ellipsoidZ(s, c, rx, ry * 1.2, len / 2 + 0.02, sides, rings, rigid(head),
      { mat, region: REG.muzzle, clampMin: my, back: 1, taper, bendY: -0.012 }));
    const cc: V3 = [0, my - 0.012, z0 + len * 0.42 + under];
    part(ctx, 'head.chin', (s) => ellipsoidZ(s, cc, rx * 0.75, 0.022 * d.sy, len * 0.42 + 0.012, Math.max(5, sides - 2), rings, rigid(jaw),
      { mat, region: REG.muzzle, clampMax: my, back: 1, taper: taper * 0.8, lw: 1.4 }));
    const tipZ = c[2] + (len / 2 + 0.02) * 0.82;
    info = {
      front: (x, y) => c[2] + (len / 2 + 0.02) * Math.sqrt(Math.max(0, 1 - (x * x) / (rx * rx * 0.5))) * 0.8 + 0 * y,
      noseAt: [0, my + ry * 1.15 - 0.012, tipZ + 0.002],
      seam: (t) => { // t ∈ [−1, 1] along the lip from the left corner over the tip to the right corner
        const a = t * Math.PI / 2;
        return [Math.sin(a) * rx * (1 - taper * 0.7), my, c[2] + Math.cos(a) * (len / 2 + 0.02) * 0.78 * (1 - 0.2 * Math.abs(t))];
      },
      halfW: rx * 0.9,
    };
  } else if (snout === 'pig') {
    const r = 0.036 * sw * d.sx;
    const y = my + 0.028 * d.sy;
    const z0 = fz(0, y) - 0.012;
    const L = 0.03 + m * 0.6;
    const flesh = MAT.flesh;
    part(ctx, 'head.muzzle', (s) => {
      const rs: Ring[] = [0, 0.6, 1].map((t) => ({ c: [0, y, z0 + L * t] as V3, rs: r * (1 + 0.1 * t), rf: r * 0.82 * (1 + 0.1 * t), skin: rigid(head) }));
      loft(s, rs, { sides: 6, front: [0, 1, 0], mat: (seg) => (seg >= 1 ? mat : mat), region: REG.muzzle, capEnd: 'flat', tangent: () => [0, 0, 1] });
      // the snout disc (flesh) + nostrils
      fan(s, [0, y, z0 + L + 0.0015], ellipse([0, y, z0 + L + 0.0015], [1, 0, 0], [0, 1, 0], r * 0.95, r * 0.8, 6), rigid(ctx.bi('nose')), { mat: flesh, region: REG.muzzle, lw: 0, hull: false });
      for (const sx of [-1, 1]) fan(s, [sx * r * 0.35, y, z0 + L + 0.003], ellipse([sx * r * 0.35, y, z0 + L + 0.003], [1, 0, 0], [0, 1, 0], r * 0.16, r * 0.26, 5),
        rigid(ctx.bi('nose')), { mat: MAT.ink, region: REG.muzzle, lw: 0, hull: false });
    });
    const cc: V3 = [0, my - 0.01, z0 + L * 0.35 + under];
    part(ctx, 'head.chin', (s) => ellipsoidZ(s, cc, r * 0.8, 0.02, 0.025, Math.max(5, sides - 2), rings, rigid(jaw), { mat, region: REG.muzzle, clampMax: my, lw: 1.4 }));
    info = { front: (x) => z0 + L * 0.5 - Math.abs(x) * 0.2, noseAt: [0, y + r * 1.1, z0 + L - 0.004], halfW: r * 0.9,
      seam: (t) => [Math.sin(t * Math.PI / 2) * r * 0.9, my, z0 + L * 0.45 * Math.cos(t * Math.PI / 2) + 0.004] };
  } else if (snout === 'beak') {
    const L = 0.035 + m * 1.2;
    const w = 0.032 * sw * d.sx, h = 0.022 * d.sy;
    const y = my + h * 0.55;
    const z0 = fz(0, y) - 0.02;
    const bare = spec.head.id === 'goose' || spec.head.id === 'owl' ? MAT.flesh : MAT.horn;
    const hooked = L < 0.07;
    part(ctx, 'head.muzzle', (s) => {
      const rs: Ring[] = [0, 0.45, 0.8, 1].map((t, i) => ({
        c: [0, y - (hooked ? 0.02 * t * t : 0.006 * t), z0 + L * t + 0.015 * t] as V3, rs: w * (1 - 0.82 * t), rf: h * (1 - 0.7 * t) * (i === 0 ? 1 : 0.9),
        skin: rigid(head), deform: (p: V3) => [p[0], Math.max(p[1], my - (hooked ? 0.02 * t * t : 0.006 * t) - 0.001), p[2]] as V3,
      }));
      loft(s, rs, { sides: 4, front: [0, 1, 0], mat: bare, region: REG.muzzle, capEnd: 'point', capEndLen: hooked ? 0.012 : 0.008, tangent: (i) => (hooked && i > 1 ? [0, -0.6, 1] : [0, -0.08, 1]) });
    });
    part(ctx, 'head.chin', (s) => {
      const rs: Ring[] = [0, 0.5, 0.85].map((t) => ({ c: [0, my - 0.008, z0 + (L * 0.85) * t + 0.012 * t + under] as V3, rs: w * 0.8 * (1 - 0.8 * t), rf: h * 0.55 * (1 - 0.6 * t),
        skin: rigid(jaw), deform: (p: V3) => [p[0], Math.min(p[1], my), p[2]] as V3 }));
      loft(s, rs, { sides: 4, front: [0, 1, 0], mat: bare, region: REG.muzzle, capEnd: 'point', capEndLen: 0.006, tangent: () => [0, -0.05, 1] });
    });
    info = { front: (x) => z0 + 0.02 - Math.abs(x), noseAt: [0, y + h * 0.4, z0 + L * 0.35], halfW: w * 0.9,
      seam: (t) => [Math.sin(t * Math.PI / 2) * w * 0.85, my, z0 + 0.02 + Math.cos(t * Math.PI / 2) * L * 0.75] };
  } else if (snout === 'toad' || snout === 'none') {
    const wide = snout === 'toad';
    const halfW = wide ? d.skullRx * 0.82 * sw : 0.03 * sw;
    if (wide) {
      const cz = fz(0, my) - 0.035;
      part(ctx, 'head.chin', (s) => ellipsoidZ(s, [0, my - 0.012, cz + under], halfW * 1.02, 0.03 * d.sy, 0.06, sides, rings, rigid(jaw),
        { mat, region: REG.muzzle, clampMax: my, lw: 1.4, back: 0.8 }));
    }
    info = { front: (x, y) => fz(x, y), noseAt: [0, my + 0.03 * d.sy, fz(0, my + 0.03 * d.sy)], halfW,
      seam: (t) => { const x = Math.sin(t * Math.PI / 2) * halfW; return [x, my, fz(x, my) + 0.0015]; } };
  } else {
    // Short muzzles: human (default cat/lion face), cat, pug, rabbit.
    const pug = snout === 'pug', rabbit = snout === 'rabbit';
    const rx = 0.046 * sw * d.sx * (pug ? 1.12 : rabbit ? 0.92 : 1);
    const ry = 0.03 * d.sy;
    const rz = 0.012 + Math.max(0.004, pug ? m * 0.4 : m);
    const cy = my + ry * 0.62;
    const cz = fz(0, cy) - rz * 0.5;
    const padDip = (p: V3): V3 => {
      // two whisker pads: a soft vertical crease at the centre bottom (ω), stronger on rabbits (split lip)
      const k = Math.exp(-Math.pow(p[0] / (rx * 0.18), 2)) * Math.max(0, 1 - (p[1] - my) / (ry * 0.9));
      return [p[0], p[1], p[2] - k * (rabbit ? 0.006 : 0.0025)];
    };
    part(ctx, 'head.muzzle', (s) => {
      const rs: Ring[] = [];
      const n = rings;
      const zb = cz - rz * 0.7;
      for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        const z = zb + (cz + rz * 0.9 - zb) * t;
        const e = Math.max(0.15, Math.sqrt(Math.max(0, 1 - Math.pow((z - cz) / (rz * 1.05), 2))));
        rs.push({ c: [0, cy, z], rs: rx * e, rf: ry * e, skin: rigid(head), deform: (p) => padDip([p[0], Math.max(p[1], my), p[2]]) });
      }
      loft(s, rs, { sides, front: [0, 1, 0], mat, region: REG.muzzle, capEnd: 'point', capEndLen: rz * 0.12, tangent: () => [0, 0, 1], jitter: 0.02, seed: ctx.seed + 7 });
    });
    const ccz = cz - 0.002 + under;
    part(ctx, 'head.chin', (s) => ellipsoidZ(s, [0, my - 0.011 * d.sy, ccz], rx * 0.7, 0.019 * d.sy, rz * 0.85, Math.max(5, sides - 2), rings, rigid(jaw),
      { mat, region: REG.muzzle, clampMax: my, lw: 1.4, back: 0.7 }));
    const frontZ = (x: number): number => cz + rz * 0.9 * Math.sqrt(Math.max(0, 1 - (x * x) / (rx * rx)));
    info = {
      front: (x, y) => Math.max(fz(x, y), frontZ(x) * (y < my + ry * 1.3 ? 1 : 0)),
      noseAt: [0, Math.max(cy + ry * 0.75, ctx.P.nose![1] - 0.012), Math.max(fz(0, cy + ry), frontZ(0) - 0.006)],
      seam: (t) => { const a = t * Math.PI / 2; const x = Math.sin(a) * rx * 0.95; return [x, my, cz + rz * 0.82 * Math.cos(a) + 0.001]; },
      halfW: rx * 0.95,
    };
  }
  ctx.mouthY = my;
  ctx.mouthHalfW = Math.min(info.halfW, rig_mouthHalfW(ctx));
  ctx.muzzleFront = info.front;
  muzzleInfo.set(ctx, info);
}
const muzzleInfo = new WeakMap<BuildCtx, MuzzleInfo>();
const rig_mouthHalfW = (ctx: BuildCtx): number => ctx.rig.faceInfo.mouthHalfW / 1;

// ─────────────────────────────────────────────────────────────────────────────────────────────── mouth ──
function mouth(ctx: BuildCtx): void {
  const info = muzzleInfo.get(ctx)!;
  const { spec } = ctx;
  const base = ctx.rig.faceInfo.base;
  const head = ctx.bi('head'), mouthB = ctx.bi('mouth');
  const mw = ctx.mouthHalfW!;
  const curve = Math.tan(base.mouthCurve * DEG);
  const asym = base.mouthAsym;
  const N = spec.o.mouth === 'toad' ? 9 : 7;
  part(ctx, 'head.mouth', (s) => {
    if (spec.o.mouth === 'o' || base.jaw > 0.05) {
      // small open "o"
      const c = info.seam(0);
      const r = Math.max(0.006, mw * 0.32);
      fan(s, add(c, [0, -r * 0.4, 0.001]), ellipse(add(c, [0, -r * 0.4, 0.001]), [1, 0, 0], [0, 1, 0], r * 0.8, r, 6), rigid(head), { mat: MAT.ink, region: REG.muzzle, lw: 0, hull: false });
    }
    // Ink lip line along the seam: corners 100 % mouth bone, centre 0 % (creatures §B7.2).
    const path: V3[] = []; const widths: number[] = []; const skins: Skin[] = [];
    for (let i = 0; i < N; i++) {
      const t = (i / (N - 1)) * 2 - 1;
      const sp = info.seam(t * Math.min(1, mw / Math.max(1e-6, info.halfW)));
      const ax = Math.abs(t);
      let y = sp[1] + curve * mw * ax * ax + asym * 0.006 * t * ax;
      if (base.omega) y += 0.004 * (1 - Math.min(1, Math.abs(Math.abs(t) - 0.5) * 2)) - 0.003 * (1 - ax);
      path.push([sp[0], y, sp[2] + 0.0018]);
      widths.push(0.0032 * (1 - 0.35 * ax));
      skins.push(ax > 0.66 ? rigid(mouthB) : ax > 0.25 ? blend(head, mouthB, (ax - 0.25) / 0.41) : rigid(head));
    }
    strip(s, path, widths, [0, 1, 0], (i) => skins[i], { mat: MAT.ink, region: REG.muzzle, lw: 0, hull: false, doubleSided: false });
    // Mouth bag (dark, revealed when the jaw opens) — behind the seam.
    const c = info.seam(0);
    const bagC: V3 = [0, ctx.mouthY! - 0.006, c[2] - 0.02];
    fan(s, bagC, ellipse(bagC, [1, 0, 0], [0, 1, 0], mw * 0.55, 0.008, 6), rigid(head), { mat: MAT.ink, region: REG.muzzle, lw: 0, hull: false });
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── nose ──
function nose(ctx: BuildCtx): void {
  const { spec } = ctx;
  if (spec.o.snout === 'beak' || spec.o.snout === 'pig' || spec.o.nose === 'nostrils' && spec.o.snout === 'pig') return;
  const info = muzzleInfo.get(ctx)!;
  const nb = ctx.bi('nose');
  const k = spec.v.noseSize;
  const at = info.noseAt;
  const mat = MAT.flesh;
  part(ctx, 'head.nose', (s) => {
    const type = spec.o.nose;
    if (type === 'potato' || type === 'drunkard') {
      const r = 0.014 * k * (type === 'drunkard' ? 1.3 : 1);
      const c: V3 = [at[0], at[1] - r * 0.4, at[2] + r * 0.2];
      const rs: Ring[] = [-0.9, -0.3, 0.3, 0.85].map((t) => ({ c: [c[0], c[1], c[2] + r * t] as V3, rs: r * Math.sqrt(1 - t * t) * 1.15 + 0.001, rf: r * Math.sqrt(1 - t * t) + 0.001, skin: rigid(nb), lw: 1 }));
      loft(s, rs, { sides: 6, front: [0, 1, 0], mat, region: REG.muzzle, capStart: 'point', capEnd: 'point', capStartLen: r * 0.1, capEndLen: r * 0.15, tangent: () => [0, 0, 1] });
    } else if (type === 'cyrano' || type === 'hooked') {
      const L = (type === 'cyrano' ? 0.06 : 0.03) * k;
      const r = 0.012 * k;
      const rs: Ring[] = [0, 0.5, 1].map((t) => ({ c: [at[0], at[1] - (type === 'hooked' ? 0.02 * t * t : 0.004 * t), at[2] - 0.006 + L * t] as V3, rs: r * (1 - 0.6 * t), rf: r * (1 - 0.6 * t), skin: rigid(nb) }));
      loft(s, rs, { sides: 5, front: [0, 1, 0], mat, region: REG.muzzle, capEnd: 'point', capEndLen: r * 0.5, tangent: () => [0, type === 'hooked' ? -0.4 : -0.05, 1] });
    } else {
      // triangle / heart / dot: an inverted triangle facet group, slightly raised (art §2.4)
      const w = (type === 'dot' ? 0.008 : 0.014) * k, h = (type === 'dot' ? 0.006 : 0.012) * k;
      const z = at[2] + 0.003;
      const top: V3 = [0, at[1] + h * 0.45, z - 0.002];
      const tl: V3 = [-w, at[1] + h * 0.5, z - 0.004], tr: V3 = [w, at[1] + h * 0.5, z - 0.004];
      const tip: V3 = [0, at[1] - h * 0.55, z - 0.001];
      const ctr: V3 = [0, at[1], z + 0.004 * k];
      const pts: V3[] = type === 'heart' ? [tl, [-w * 0.45, at[1] + h * 0.75, z - 0.003], top, [w * 0.45, at[1] + h * 0.75, z - 0.003], tr, tip] : [tl, top, tr, tip];
      // fan CCW seen from +Z: order left → top → right → tip is clockwise in x-y seen from +Z, so reverse
      const ring = [...pts].reverse();
      fan(s, ctr, ring, rigid(nb), { mat, region: REG.muzzle, lw: 1.1 });
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── eyes ──
function eyeFrame(g: EyeGeom): { ex: V3; ey: V3; ez: V3 } {
  const ez = nrm(g.ez as V3); const ex = nrm(g.ex as V3); const ey = nrm(cross(ez, ex));
  return { ex, ey, ez };
}

function eyes(ctx: BuildCtx): void {
  const { spec, cfg } = ctx;
  const base = ctx.rig.faceInfo.base;
  const stalks = spec.flags.eyeStalks;
  const cyclops = spec.o.eyes === 'cyclops';
  const sides: Array<1 | -1> = cyclops ? [1] : [1, -1];
  for (const side of sides) {
    const i = side > 0 ? 0 : 1;
    const S = side > 0 ? 'L' : 'R';
    let g = ctx.rig.eyes[i];
    const eyeBone = ctx.bi(`eye_${S}` as BoneName);
    const lidBone = ctx.bi(`lid_${S}` as BoneName);
    let holder = ctx.bi('head');
    if (stalks) {
      // eyes on the stalk tips (ear_x_02), facing forward
      const tip = ctx.P[`ear_${S}_02` as BoneName]!;
      const t1 = ctx.P[`ear_${S}_01` as BoneName]!;
      const dir = nrm(sub(tip, t1));
      const c = add(tip, scl(dir, ctx.rig.eyes[i].ry * 1.6));
      const off = sub(c, g.center as V3);
      g = { ...g, center: c as [number, number, number], lidPivot: add(g.lidPivot as V3, off) as [number, number, number], ex: [1, 0, 0], ez: [0, 0, 1] };
      holder = ctx.bi(`ear_${S}_02` as BoneName);
    }
    const { ex, ey, ez } = eyeFrame(g);
    const C = g.center as V3;
    const P = (x: number, y: number, z: number): V3 => add(C, add(add(scl(ex, x * side), scl(ey, y)), scl(ez, z)));
    const n = cfg.eyeSides;
    const tilt = g.tilt * DEG;
    const outline: Array<[number, number]> = [];
    for (let k = 0; k < n; k++) {
      const a = (k / n) * TAU;
      let x = Math.cos(a) * g.rx, y = Math.sin(a) * g.ry * (g.shape === 'almond' || g.shape === 'human' || g.shape === 'sad' ? 1 - 0.16 * Math.cos(a) ** 2 : 1);
      // outer corner (x > 0 in eye space) tilts by `tilt`
      y += Math.max(0, x / g.rx) * Math.tan(tilt) * g.rx * 0.5;
      x *= 1; outline.push([x, y]);
    }
    const lidSkin = rigid(stalks ? holder : lidBone);
    const scleraSkin = rigid(holder);
    const irisSkin = rigid(stalks ? holder : eyeBone);
    const patched = spec.flags.eyepatch && side > 0;
    part(ctx, `head.eye_${S}`, (s) => {
      if (stalks) {
        // a full eyeball behind the disc
        const rr = Math.max(g.rx, g.ry) * 1.05;
        const rs: Ring[] = [-0.95, -0.5, 0.2, 0.75].map((t) => ({ c: P(0, 0, -rr * 0.55 + rr * t), rs: rr * Math.sqrt(1 - t * t) + 0.0008, rf: rr * Math.sqrt(1 - t * t) + 0.0008, skin: scleraSkin, lw: 0.8 }));
        loft(s, rs, { sides: 6, front: [0, 1, 0], mat: MAT.sclera, region: REG.skull, capStart: 'point', capEnd: 'point', capStartLen: rr * 0.05, capEndLen: rr * 0.25, tangent: () => ez });
      }
      // Sclera: a shallow cone (dome) — unshaded, no outline (art §2.7).
      const orient = <T,>(pts: T[]): T[] => (side > 0 ? pts : [...pts].reverse());
      fan(s, P(0, 0, 0), orient(outline.map(([x, y]) => P(x, y, 0))), scleraSkin, { mat: MAT.sclera, region: REG.skull, lw: 0, hull: false }, scl(ez, g.dome));
      if (!patched) {
        const [gx, gy] = gazeOffset(g, base.gazeYaw[i] * side, base.gazePitch);
        const lift = (r: number): number => g.dome * Math.max(0, 1 - r / Math.min(g.rx, g.ry)) + 0.0011;
        const ic: [number, number] = [gx, gy];
        const iris: V3[] = [];
        for (let k = 0; k < n; k++) {
          const a = (k / n) * TAU;
          let x = ic[0] + Math.cos(a) * g.ri, y = ic[1] + Math.sin(a) * g.ri;
          // keep the iris inside the sclera outline (radial clamp to the outline ellipse)
          const e = (x / (g.rx * 0.98)) ** 2 + (y / (g.ry * 0.98)) ** 2;
          if (e > 1) { const k2 = 1 / Math.sqrt(e); x *= k2; y *= k2; }
          iris.push(P(x, y, lift(g.ri)));
        }
        fan(s, P(ic[0], ic[1], g.dome + 0.0012), orient(iris), irisSkin, { mat: MAT.iris, region: REG.skull, lw: 0, hull: false });
        // Pupil
        const pr = g.ri * base.pupil[i];
        const pz = g.dome + 0.0022;
        const pupilShape = spec.o.pupil;
        const pupilPts = (cx: number, cy: number, rx: number, ry: number, m: number, diamond = false): V3[] => {
          const out: V3[] = [];
          for (let k = 0; k < m; k++) {
            const a = (k / m) * TAU;
            const c2 = Math.cos(a), s2 = Math.sin(a);
            const kk = diamond ? 1 / (Math.abs(c2) + Math.abs(s2)) : 1;
            out.push(P(cx + c2 * rx * kk, cy + s2 * ry * kk, Math.max(lift(Math.hypot(c2 * rx, s2 * ry) + Math.hypot(cx, cy) - Math.hypot(gx, gy)), pz - 0.0004)));
          }
          return out;
        };
        const pm = Math.max(6, Math.min(12, n));
        const pupils: Array<[number, number, number, number, boolean]> = [];
        if (pupilShape === 'slit') pupils.push([gx, gy, pr * 0.32, Math.min(g.ri * 0.92, pr * 2.0), false]);
        else if (pupilShape === 'bar') pupils.push([gx, gy, Math.min(g.ri * 0.85, pr * 1.6), pr * 0.5, false]);
        else if (pupilShape === 'dot') pupils.push([gx, gy, pr * 0.5, pr * 0.5, false]);
        else if (pupilShape === 'lozenge') pupils.push([gx, gy, pr * 0.8, pr * 1.2, true]);
        else if (pupilShape === 'double') { pupils.push([gx - pr * 0.55, gy, pr * 0.45, pr * 0.45, false]); pupils.push([gx + pr * 0.55, gy, pr * 0.45, pr * 0.45, false]); }
        else pupils.push([gx, gy, pr, pr, false]);
        for (const [cx, cy, rx, ry, dia] of pupils) fan(s, P(cx, cy, pz + 0.0004), orient(pupilPts(cx, cy, rx, ry, pm, dia)), irisSkin, { mat: MAT.ink, region: REG.skull, lw: 0, hull: false });
      }
      // Upper lid: a cylinder shell about the eye x-axis through the lid pivot (rotation about it closes the eye).
      const cover = patched ? 1 : base.lid[i];
      const thEdge = lidAngle(g, cover);
      const thTop = Math.acos(Math.min(0.995, (g.ry * 1.35) / g.lidR));
      const cols = Math.max(4, Math.round(n / 2) + 1);
      const rowsA: number[] = [];
      const lr = cfg.lidRings;
      for (let r = 0; r <= lr; r++) rowsA.push(thTop + (thEdge - thTop) * (r / lr) * 0.94);
      const band = 0.0042 / g.lidR;
      const piv = g.lidPivot as V3;
      const Q = (x: number, th: number, edgeClamp: number): V3 => {
        const t = Math.min(th, edgeClamp);
        return add(piv, add(add(scl(ex, x * side), scl(ey, g.lidR * Math.cos(t))), scl(ez, g.lidR * Math.sin(t))));
      };
      const grid: number[][] = [];
      const xs: number[] = [];
      for (let c = 0; c < cols; c++) xs.push((-1 + (2 * c) / (cols - 1)) * g.rx * 1.12);
      for (let c = 0; c < cols; c++) {
        const x = xs[c];
        // the lid line follows the sclera's top outline near the corners (where the open lid would float above the eye)
        const h = g.ry * Math.sqrt(Math.max(0, 1 - Math.min(1, (x / (g.rx * 1.05)) ** 2))) + Math.max(0, x / g.rx) * Math.tan(tilt) * g.rx * 0.5;
        const thOutline = Math.acos(Math.max(-0.999, Math.min(0.999, (h * 1.02 + 0.0008) / g.lidR)));
        const thE = Math.max(thEdge, thOutline);
        const col: number[] = [];
        const rows = [...rowsA.map((t) => Math.min(t, thE - band)), thE - band, thE];
        for (let r = 0; r < rows.length; r++) {
          const p = Q(x, rows[r], thE);
          col.push(s.vert({ p, u: r * 0.01, v: c / (cols - 1), ...lidSkin, lw: r === rows.length - 1 ? 1.2 : 0.6 }));
        }
        grid.push(col);
      }
      const R = grid[0].length;
      for (let c = 0; c + 1 < cols; c++) {
        for (let r = 0; r + 1 < R; r++) {
          const a = grid[c][r], b = grid[c + 1][r], cc = grid[c + 1][r + 1], dd = grid[c][r + 1];
          const isBand = r === R - 2;
          const info = { mat: isBand ? MAT.ink : ctx.headMat, region: REG.skull, hull: false, edge: isBand ? 0 : 0 };
          // orientation: outward = +ez-ish; for the right eye the x axis is mirrored
          if (side > 0) { s.tri(a, cc, b, info); s.tri(a, dd, cc, info); } else { s.tri(a, b, cc, info); s.tri(a, cc, dd, info); }
        }
      }
      if (patched) {
        // eyepatch: a leather disc over the closed left eye
        const pc = P(0, 0, g.dome + 0.006);
        fan(s, pc, orient(ellipse(pc, scl(ex, side), ey, g.rx * 1.35, g.ry * 1.45, 7)), scleraSkin, { mat: MAT.leather, region: REG.extra, lw: 1 });
      }
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── brows ──
function brows(ctx: BuildCtx): void {
  const { spec, cfg } = ctx;
  const type = spec.o.brows;
  if (type === 'none' || spec.flags.eyeStalks) return;
  const base = ctx.rig.faceInfo.base;
  const front = ctx.skullFront!;
  const thick = 0.0058 * spec.v.browThick;
  for (const side of [1, -1] as const) {
    const i = side > 0 ? 0 : 1;
    const S = side > 0 ? 'L' : 'R';
    if (type === 'unibrow' && side < 0) continue;
    const bone = ctx.bi(`brow_${S}` as BoneName);
    const piv = ctx.P[`brow_${S}` as BoneName]!;
    const g = ctx.rig.eyes[i];
    const len = Math.max(g.rx * 2.1, 0.05) * (type === 'elder' ? 1.25 : type === 'tufts' ? 0.9 : 1);
    const ang = base.browAngle[i] * 20 * DEG * side;      // + = inner end down for L
    const raise = base.browRaise[i] * 0.012;
    const facets = cfg.browFacets + 1;
    part(ctx, `head.brow_${S}`, (s) => {
      const pts: V3[] = []; const w: number[] = [];
      if (type === 'unibrow') {
        const L = ctx.rig.eyes[0], R = ctx.rig.eyes[1];
        const x0 = R.center[0] - R.rx, x1 = L.center[0] + L.rx;
        for (let k = 0; k <= 4; k++) {
          const t = k / 4; const x = x0 + (x1 - x0) * t;
          const y = piv[1] + raise + 0.004 * Math.sin(t * Math.PI) - 0.003 * (1 - Math.abs(t - 0.5) * 2);
          pts.push([x, y, front(x, y) + 0.003]); w.push(thick * 1.1);
        }
        const L0 = ctx.bi('brow_L'), R0 = ctx.bi('brow_R');
        strip(s, pts, w, [0, 1, 0], (k) => (k < 2 ? rigid(R0) : k > 2 ? rigid(L0) : blend(R0, L0, 0.5)), { mat: MAT.ink, region: REG.skull, lw: 0.4, hull: false });
        return;
      }
      for (let k = 0; k < facets; k++) {
        const t = k / (facets - 1);             // 0 = inner end, 1 = outer end
        const xLocal = (t - 0.5) * len;
        let yLocal = 0.0035 * Math.sin(t * Math.PI);  // arch
        if (type === 'worried') yLocal += 0.006 * (1 - t);
        if (type === 'elder') yLocal -= 0.008 * t * t;
        // rotate about the pivot by the base angle (inner end is toward the midline: −x for L)
        const xr = xLocal * Math.cos(ang) - yLocal * Math.sin(ang);
        const yr = xLocal * Math.sin(-ang) * -1 + yLocal * Math.cos(ang);
        const x = piv[0] + side * xr;
        const y = piv[1] + raise + yr;
        pts.push([x, y, front(x, y) + 0.0035]);
        const taper = type === 'angry' ? 1.7 - 1.1 * t : type === 'calligraphic' ? 0.4 + Math.sin(t * Math.PI) * 1.1 : type === 'bushy' ? 1.7 : 1;
        w.push(thick * 0.5 * taper);
      }
      if (type === 'scar') { pts.splice(1, 1); w.splice(1, 1); }
      strip(s, pts, w, (k) => nrm(cross(nrm(sub(pts[Math.min(pts.length - 1, k + 1)], pts[Math.max(0, k - 1)])), [0, 0, 1])), () => rigid(bone),
        { mat: MAT.ink, region: REG.skull, lw: 0.4, hull: false });
      if (type === 'tufts' || type === 'bushy') {
        for (let k = 0; k < 3; k++) {
          const p = pts[Math.min(pts.length - 1, k)];
          strip(s, [p, add(p, [side * 0.004 * (k - 1), 0.012, 0.002])], [thick * 0.5, 0.0005], [1, 0, 0], () => rigid(bone), { mat: MAT.ink, region: REG.skull, lw: 0.3, hull: false });
        }
      }
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── teeth ──
function teeth(ctx: BuildCtx): void {
  const { spec, cfg } = ctx;
  if (!cfg.teeth && !['buck', 'tusks', 'fangs', 'single', 'gap'].includes(spec.o.teeth)) return;
  const t = spec.o.teeth;
  if (t === 'hidden') return;
  const info = muzzleInfo.get(ctx)!;
  const head = ctx.bi('head'), jaw = ctx.bi('jaw');
  const my = ctx.mouthY!;
  const c = info.seam(0);
  part(ctx, 'head.teeth', (s) => {
    const tooth = (x: number, w: number, h: number, bone: number, down = true, zOff = 0): void => {
      const z = (info.seam(x / Math.max(1e-6, info.halfW))[2] || c[2]) + zOff;
      box(s, [x, my + (down ? -h / 2 + 0.002 : h / 2 - 0.002), z + 0.001], [w / 2, 0, 0], [0, h / 2, 0], [0, 0, 0.0025], rigid(bone));
    };
    if (t === 'buck' || t === 'gap') { const g = t === 'gap' ? 0.006 : 0.0012; tooth(-(0.0055 + g / 2), 0.010, 0.016, head); tooth(0.0055 + g / 2, 0.010, 0.016, head); }
    else if (t === 'single') tooth(0.006, 0.009, 0.014, head);
    else if (t === 'fangs') for (const sx of [-1, 1]) cone(s, [sx * info.halfW * 0.45, my + 0.002, c[2] - 0.002], [0, -1, 0.15], 0.004, 0.014, rigid(head));
    else if (t === 'tusks') for (const sx of [-1, 1]) cone(s, [sx * info.halfW * 0.8, my - 0.004, c[2] - 0.006], [sx * 0.25, 1, 0.3], 0.005, 0.02, rigid(jaw));
    else if (t === 'shark') for (let k = -2; k <= 2; k++) cone(s, [k * info.halfW * 0.25, my + 0.001, c[2] - 0.004 - Math.abs(k) * 0.002], [0, -1, 0.1], 0.0035, 0.008, rigid(head));
    else { // crooked / gold / human: an upper row just behind the lip (visible when the jaw opens)
      for (let k = -1; k <= 1; k++) tooth(k * 0.0075, 0.0068, 0.008 + (t === 'crooked' ? (k + 1) * 0.002 : 0), head, true, -0.004);
    }
  });
  function box(s: Surface, c0: V3, ax: V3, ay: V3, az: V3, skin: Skin): void {
    const corners: V3[] = [];
    for (const z of [-1, 1]) for (const y of [-1, 1]) for (const x of [-1, 1]) corners.push(add(c0, add(add(scl(ax, x), scl(ay, y)), scl(az, z))));
    const idx = corners.map((p) => s.vert({ p, u: 0, v: 0, ...skin, lw: 0.8 }));
    const mat = t === 'gold' ? MAT.gold : MAT.horn;
    for (const [a, b, cc, dd] of [[0, 2, 3, 1], [4, 5, 7, 6], [0, 1, 5, 4], [2, 6, 7, 3], [0, 4, 6, 2], [1, 3, 7, 5]]) {
      s.tri(idx[a], idx[b], idx[cc], { mat, region: REG.muzzle }); s.tri(idx[a], idx[cc], idx[dd], { mat, region: REG.muzzle });
    }
  }
}

/** A small 4-sided cone from `base` along `dir`. */
function cone(s: Surface, base: V3, dir: V3, r: number, L: number, skin: Skin, mat = MAT.horn, sides = 4): void {
  const d = nrm(dir);
  const rs: Ring[] = [{ c: base, rs: r, rf: r, skin }, { c: add(base, scl(d, L * 0.5)), rs: r * 0.6, rf: r * 0.6, skin }];
  loft(s, rs, { sides, front: Math.abs(d[1]) > 0.9 ? [0, 0, 1] : [0, 1, 0], mat, region: REG.extra, capStart: 'flat', capEnd: 'point', capEndLen: L * 0.5, tangent: () => d });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── whiskers ──
function whiskers(ctx: BuildCtx): void {
  const { spec, cfg } = ctx;
  const type = spec.o.whiskers;
  if (type === 'none' || !cfg.whiskers) return;
  const info = muzzleInfo.get(ctx)!;
  const nb = ctx.bi('nose');
  const count = Math.max(1, Math.round(spec.v.whiskerCount));
  const L = 0.085 * spec.v.whiskerLen;
  part(ctx, 'head.whiskers', (s) => {
    for (const side of [1, -1]) {
      for (let k = 0; k < count; k++) {
        const t = count === 1 ? 0.5 : k / (count - 1);
        const p0 = info.seam(side * 0.62);
        const start: V3 = [p0[0], p0[1] + 0.008 + t * 0.008, p0[2] - 0.004];
        let slope = (t - 0.5) * 0.35;
        if (type === 'fan') slope = (t - 0.5) * 0.9;
        const pts: V3[] = [];
        for (let j = 0; j <= 3; j++) {
          const u = j / 3;
          let y = slope * L * u;
          if (type === 'curled') y += 0.025 * u * u;
          if (type === 'droopy') y -= 0.03 * u * u;
          if (type === 'broken' && side > 0 && k === 0 && u > 0.5) y -= 0.03 * (u - 0.5);
          if (type === 'tendrils') y += 0.012 * Math.sin(u * 6);
          pts.push([start[0] + side * L * u, start[1] + y, start[2] - 0.02 * u]);
        }
        strip(s, pts, [0.0013, 0.0011, 0.0009, 0.0004], [0, 1, 0], () => rigid(nb), { mat: MAT.ink, region: REG.muzzle, lw: 0, hull: false });
      }
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── ears ──
const EAR_PROFILES: Readonly<Record<string, Array<[number, number]>>> = {
  triangle: [[-1, 0], [1, 0], [0.62, 0.45], [0.16, 1], [-0.36, 0.52]],
  tall: [[-0.8, 0], [0.8, 0], [0.42, 0.5], [0.08, 1], [-0.34, 0.5]],
  rounded: [[-1, 0], [1, 0], [0.95, 0.45], [0.55, 0.88], [0, 1], [-0.55, 0.88], [-0.95, 0.45]],
  folded: [[-1, 0], [1, 0], [0.7, 0.5], [0.1, 1], [-0.5, 0.55]],
  lynx: [[-0.95, 0], [0.95, 0], [0.55, 0.5], [0.1, 1], [-0.4, 0.5]],
  torn: [[-1, 0], [1, 0], [0.72, 0.32], [0.34, 0.4], [0.48, 0.56], [0.16, 1], [-0.36, 0.52]],
  mouse: [[-0.7, 0], [0.7, 0], [1.25, 0.35], [1.2, 0.75], [0.6, 1.05], [-0.1, 1.05], [-0.75, 0.8], [-0.95, 0.35]],
  long: [[-0.62, 0], [0.62, 0], [0.82, 0.42], [0.55, 0.85], [0.05, 1], [-0.5, 0.85], [-0.8, 0.42]],
  lop: [[-0.62, 0], [0.62, 0], [0.85, 0.45], [0.6, 0.88], [0, 1], [-0.6, 0.88], [-0.85, 0.45]],
  hound: [[-0.6, 0], [0.6, 0], [0.9, 0.5], [0.75, 0.92], [0, 1], [-0.7, 0.9], [-0.85, 0.45]],
  goat: [[-0.55, 0], [0.55, 0], [0.75, 0.45], [0.3, 0.92], [0, 1], [-0.35, 0.9], [-0.75, 0.45]],
  human: [[-0.4, 0], [0.4, 0], [0.9, 0.35], [0.9, 0.8], [0.3, 1], [-0.4, 0.85], [-0.6, 0.4]],
};
const EAR_WIDTH: Readonly<Record<string, number>> = { triangle: 0.42, tall: 0.34, rounded: 0.5, folded: 0.45, lynx: 0.4, torn: 0.42, mouse: 0.62, long: 0.2, lop: 0.24,
  hound: 0.32, goat: 0.26, human: 0.4, owlTufts: 0.2 };

function ears(ctx: BuildCtx): void {
  const { spec, cfg } = ctx;
  const type = spec.flags.eyeStalks ? 'stalks' : spec.o.ears;
  if (type === 'none') return;
  for (const side of [1, -1] as const) {
    const S = side > 0 ? 'L' : 'R';
    const b1 = ctx.bi(`ear_${S}_01` as BoneName), b2 = ctx.bi(`ear_${S}_02` as BoneName);
    const p1 = ctx.P[`ear_${S}_01` as BoneName]!, p2 = ctx.P[`ear_${S}_02` as BoneName]!;
    const dvec = sub(p2, p1);
    const L = Math.hypot(dvec[0], dvec[1], dvec[2]) / 0.75;
    const d = nrm(dvec);
    const skinV = (v: number): Skin => (v < 0.35 ? rigid(b1) : v > 0.7 ? rigid(b2) : blend(b1, b2, (v - 0.35) / 0.35));
    part(ctx, `head.ear_${S}`, (s) => {
      if (type === 'stalks') {
        const rs: Ring[] = [0, 0.4, 0.75, 1].map((t) => ({ c: add(p1, scl(d, L * t * 0.98)), rs: 0.014 - 0.006 * t, rf: 0.014 - 0.006 * t, skin: skinV(t), lw: 1 }));
        loft(s, rs, { sides: Math.max(4, cfg.limbSides - 1), front: [0, 0, 1], mat: ctx.headMat, region: REG.ears, capStart: 'none', capEnd: 'flat' });
        return;
      }
      if (type === 'owlTufts') {
        for (let k = 0; k < 3; k++) {
          const dd = nrm(add(d, [side * (k - 1) * 0.25, 0, -0.1 * k]));
          strip(s, [p1, add(p1, scl(dd, L * 0.6)), add(p1, scl(dd, L))], [0.012, 0.009, 0.001], [1, 0, 0], (i) => skinV(i / 2), { mat: ctx.headMat, region: REG.ears, lw: 0.9 });
        }
        return;
      }
      const prof = EAR_PROFILES[type] ?? EAR_PROFILES.triangle;
      const hw = (EAR_WIDTH[type] ?? 0.42) * L * 0.5 * (type === 'long' || type === 'lop' ? 1.6 : 1) + 0.006;
      let w = nrm(cross(d, [0, 0, 1]));
      if (Math.hypot(...cross(d, [0, 0, 1])) < 0.2) w = [1, 0, 0];
      const n0 = nrm(cross(w, d));
      // tip leans outward: mirror the profile's x for the right ear (its outer side is −w)
      const outer = side > 0 ? 1 : -1;
      const depth = 0.009 + 0.004 * (L / 0.2);
      const origin = sub(p1, scl(d, L * 0.12));
      const folded = type === 'folded';
      const map = (x: number, y: number): [number, number] => [x * outer * hw, y * L * 1.12];
      const profile = prof.map(([x, y]) => map(x, y));
      // fold: bend the top half forward (art: folded −80° at 50 %)
      const ax = w, ay = d, az = n0;
      const res = extrudeProfile(s, origin, ax, ay, az, profile, depth, (_x, y) => skinV(y / (L * 1.12)), { mat: ctx.headMat, region: REG.ears, lw: 1, backMat: ctx.headMat });
      void res;
      if (folded) foldVerts(s, origin, ax, ay, az, L * 0.6);
      // inner ear (flesh), inset on the front face
      const inner = prof.map(([x, y]): [number, number] => map(x * 0.58, 0.1 + y * 0.72));
      const inC = add(origin, scl(az, depth / 2 + 0.0012));
      const innerPts = inner.map(([x, y]) => add(inC, add(scl(ax, x), scl(ay, y))));
      const cx = inner.reduce((a, p) => a + p[0], 0) / inner.length, cy = inner.reduce((a, p) => a + p[1], 0) / inner.length;
      const ccw = signedArea(inner) > 0;
      fan(s, add(inC, add(scl(ax, cx), scl(ay, cy))), ccw === (dotV(cross(ax, ay), az) > 0) ? innerPts : [...innerPts].reverse(),
        (p) => skinV(dotV(sub(p, origin), ay) / (L * 1.12)), { mat: MAT.flesh, region: REG.ears, lw: 0.6, hull: false }, scl(az, -0.0008));
      if (folded) foldVerts(s, origin, ax, ay, az, L * 0.6);
      if (type === 'lynx') strip(s, [add(origin, scl(ay, L * 1.08)), add(origin, scl(ay, L * 1.38))], [0.004, 0.0005], ax, () => rigid(b2), { mat: MAT.ink, region: REG.ears, lw: 0.6 });
      if (ctx.spec.flags.earPockets && type !== 'none') {
        const sleeve = prof.map(([x, y]): [number, number] => map(x * 1.12, Math.min(y, 0.55) * 1.0));
        extrudeProfile(s, sub(origin, scl(ay, 0.004)), ax, ay, az, sleeve, depth + 0.006, (_x, y) => skinV(y / (L * 1.12)), { mat: MAT.dye0, region: REG.extra, lw: 1 });
      }
    });
  }
}
const signedArea = (pts: Array<[number, number]>): number => { let a = 0; for (let k = 0; k < pts.length; k++) { const [x0, y0] = pts[k]; const [x1, y1] = pts[(k + 1) % pts.length]; a += x0 * y1 - x1 * y0; } return a; };
const dotV = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
/** Bends every vertex of the surface above `h` along `ay` forward (toward +az) by up to 80°. */
function foldVerts(s: Surface, origin: V3, ax: V3, ay: V3, az: V3, h: number): void {
  for (const v of s.verts) {
    const rel = sub(v.p, origin);
    const y = dotV(rel, ay);
    if (y <= h || (v as { folded?: boolean }).folded) continue;
    const z = dotV(rel, az), x = dotV(rel, ax);
    const a = -80 * DEG;
    const dy = y - h;
    const ny = h + dy * Math.cos(a) - z * Math.sin(a);
    const nz = dy * Math.sin(-a) + z * Math.cos(a);
    v.p = add(origin, add(add(scl(ax, x), scl(ay, ny)), scl(az, nz)));
    (v as { folded?: boolean }).folded = true;
  }
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── horns ──
function horns(ctx: BuildCtx): void {
  const { spec, d } = ctx;
  const t = spec.o.horns;
  if (t === 'none') return;
  const head = ctx.bi('head');
  const k = spec.v.hornSize;
  const top = d.crown;
  part(ctx, 'head.horns', (s) => {
    for (const side of t === 'unicorn' ? [0] : [1, -1]) {
      const base: V3 = t === 'unicorn' ? [0, top - 0.06 * d.sy, d.headZ + d.skullRz * 0.75] : [side * d.skullRx * 0.42, top - 0.035 * d.sy, d.headZ + 0.02];
      const pts: V3[] = [];
      let r0 = 0.018 * k, r1 = 0.003;
      const N = 6;
      for (let i = 0; i <= N; i++) {
        const u = i / N;
        let p: V3;
        if (t === 'goat') { const a = u * 120 * DEG; p = add(base, [side * 0.03 * k * u, Math.sin(a) * 0.09 * k * 0.7, -(1 - Math.cos(a)) * 0.075 * k]); }
        else if (t === 'ram') { const a = u * 1.25 * TAU; const rr = 0.05 * k * (1 - 0.5 * u); p = add(base, [side * (0.02 + 0.06 * u) * k, Math.sin(a) * rr, -Math.cos(a) * rr + rr - 0.02 * u]); r0 = 0.02 * k; }
        else if (t === 'nubs') { p = add(base, [side * 0.004 * u, 0.025 * k * u, 0.004 * u]); r0 = 0.012 * k; r1 = 0.004; }
        else if (t === 'unicorn') { p = add(base, [0, 0.13 * k * u, 0.05 * k * u]); r0 = 0.014 * k; r1 = 0.001; }
        else if (t === 'devil') { const a = u * 50 * DEG; p = add(base, [side * Math.sin(a) * 0.035 * k, 0.055 * k * u, 0.01 * u]); r0 = 0.012 * k; r1 = 0.001; }
        else { /* antlers: main beam */ p = add(base, [side * 0.06 * k * u, 0.1 * k * u, -0.03 * u]); r0 = 0.01 * k; r1 = 0.003; }
        pts.push(p);
      }
      const rs: Ring[] = pts.map((p, i) => ({ c: p, rs: r0 + (r1 - r0) * (i / N), rf: (r0 + (r1 - r0) * (i / N)) * (t === 'goat' ? 0.8 : 1), skin: rigid(head),
        shape: t === 'goat' || t === 'ram' ? (th: number) => 1 + 0.12 * Math.cos(th * TAU * 2) * (i % 2 ? 1 : -1) : undefined }));
      loft(s, rs, { sides: t === 'unicorn' ? 5 : 4, front: [0, 0, 1], mat: MAT.horn, region: REG.extra, capStart: 'none', capEnd: 'point', capEndLen: 0.006 });
      if (t === 'antlers') for (const f of [0.45, 0.75]) {
        const b = pts[Math.round(f * N)];
        cone(s, b, [side * 0.2, 1, 0.3], 0.006 * k, 0.05 * k, rigid(head));
      }
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────────── hair / beard ──
function hair(ctx: BuildCtx): void {
  const { spec, d, rig } = ctx;
  const t = spec.o.hair;
  if (t === 'none') return;
  const head = ctx.bi('head');
  const prof = rig.skull;
  const mat = ctx.headMat;
  part(ctx, 'head.hair', (s) => {
    if (t === 'tonsure' || t === 'bowl') {
      // a ring of fur around the crown (tonsure: bald crown; bowl: a cap down to the brows)
      const f0 = t === 'tonsure' ? 0.66 : 0.6, f1 = t === 'tonsure' ? 0.86 : 0.97;
      const rings: Ring[] = [f0, (f0 + f1) / 2, f1].map((f) => { const r = prof.ring(f); return { c: [0, r.y, r.cz] as V3, rs: r.rs * 1.08 + 0.006, rf: r.rf * 1.08 + 0.006, skin: rigid(head), lw: 1 }; });
      loft(s, rings, { sides: 10, front: [0, 0, 1], stagger: true, mat, region: REG.extra, capEnd: t === 'bowl' ? 'point' : 'none', capEndLen: 0.02, tangent: () => [0, 1, 0], jitter: 0.06, seed: ctx.seed + 3 });
    } else if (t === 'mane') {
      // heraldic curl clumps around the face, neck and shoulders (lion; concept: few big clumps)
      const n = 9;
      for (let k = 0; k < n; k++) {
        const a = (-0.62 + (1.24 * k) / (n - 1)) * Math.PI; // around the face, behind
        const f = 0.15 + 0.7 * (0.5 + 0.5 * Math.cos(a));
        const r = prof.ring(Math.min(0.92, f));
        const c: V3 = [Math.sin(a) * r.rs * 1.1, r.y + 0.01, r.cz - Math.cos(a) * r.rf * 0.2 - 0.03];
        const R = 0.045 * d.sx;
        const rs: Ring[] = [-0.8, -0.2, 0.5].map((t2) => ({ c: add(c, [0, 0, R * t2]) as V3, rs: R * Math.sqrt(1 - t2 * t2) + 0.002, rf: R * Math.sqrt(1 - t2 * t2) * 1.2 + 0.002, skin: rigid(head) }));
        loft(s, rs, { sides: 5, front: [Math.sin(a), 0, 1], mat, region: REG.extra, capStart: 'point', capEnd: 'point', capStartLen: R * 0.4, capEndLen: R * 0.6, tangent: () => [Math.sin(a) * 0.3, -0.2, -1] });
      }
      const neck = ctx.ob('neck');
      if (neck >= 0) {
        const n2 = ctx.P.neck!;
        const rings: Ring[] = [0, 0.5, 1].map((t2) => ({ c: [0, n2[1] - 0.05 + t2 * 0.12, n2[2] - 0.01] as V3, rs: 0.1 * d.sx * (1.05 - 0.2 * t2), rf: 0.09 * (1.05 - 0.25 * t2), skin: blend(neck, head, t2), lw: 1 }));
        loft(s, rings, { sides: 9, front: [0, 0, 1], mat, region: REG.extra, tangent: () => [0, 1, 0], jitter: 0.1, seed: ctx.seed + 9, stagger: true });
      }
    } else if (t === 'curls') {
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * TAU;
        const r = prof.ring(0.86);
        const c: V3 = [Math.cos(a) * r.rs * 0.75, r.y + 0.02, r.cz + Math.sin(a) * r.rf * 0.6];
        const R = 0.022;
        loft(s, [-0.7, 0, 0.7].map((t2) => ({ c: add(c, [0, R * t2, 0]) as V3, rs: R * Math.sqrt(1 - t2 * t2) + 0.002, rf: R * Math.sqrt(1 - t2 * t2) + 0.002, skin: rigid(head) })),
          { sides: 5, front: [0, 0, 1], mat, region: REG.extra, capStart: 'point', capEnd: 'point', capStartLen: R * 0.3, capEndLen: R * 0.3, tangent: () => [0, 1, 0] });
      }
    } else {
      // tuft / crest / centre part / comb-over: a few strips on the crown
      const n = t === 'crest' ? 4 : 3;
      for (let k = 0; k < n; k++) {
        const r = prof.ring(0.9);
        const x = (k - (n - 1) / 2) * 0.012;
        const z = r.cz + (t === 'crest' ? -0.02 + k * 0.015 : 0.02);
        const p0: V3 = [x, d.crown - 0.008, z];
        const dir: V3 = t === 'combOver' ? [0.8, 0.25, 0] : t === 'centrePart' ? [Math.sign(x || 1) * 0.9, 0.1, 0.2] : [x * 8, 1, 0.3];
        strip(s, [p0, add(p0, scl(nrm(dir), 0.03)), add(p0, scl(nrm(add(dir, [0, -0.5, 0])), 0.05))], [0.009, 0.007, 0.001], [0, 0, 1], () => rigid(head), { mat, region: REG.extra, lw: 0.8 });
      }
    }
  });
}

function beard(ctx: BuildCtx): void {
  const { spec, d } = ctx;
  const t = spec.o.beard, mu = spec.o.mustache;
  const info = muzzleInfo.get(ctx)!;
  const jaw = ctx.bi('jaw'), head = ctx.bi('head');
  const beardBone = ctx.ob('beard');
  const kLen = spec.v.beardLen;
  if (t !== 'none' && !spec.flags.beardHidden) {
    part(ctx, 'head.beard', (s) => {
      const c = info.seam(0);
      const top: V3 = [0, ctx.mouthY! - 0.02, c[2] - 0.006];
      const L = (t === 'goatee' ? 0.06 : t === 'monk' || t === 'waist' ? (t === 'waist' ? 0.32 : 0.16) : t === 'stubble' ? 0.01 : 0.1) * kLen;
      const W = t === 'boyar' || t === 'chops' ? 0.06 * d.sx : t === 'goatee' ? 0.018 : 0.035 * d.sx;
      const sk = (u: number): Skin => (beardBone >= 0 ? blend(jaw, beardBone, u) : rigid(jaw));
      if (t === 'chops') {
        for (const side of [1, -1]) {
          const r = ctx.rig.skull.ring(0.25);
          const p: V3 = [side * r.rs * 0.92, r.y, r.cz + r.rf * 0.35];
          strip(s, [add(p, [0, 0.03, 0]), p, add(p, [side * 0.01, -0.035, 0.01])], [0.014, 0.018, 0.004], [0, 0, 1], () => rigid(head), { mat: ctx.headMat, region: REG.extra, lw: 1 });
        }
        return;
      }
      const N = 4;
      const rs: Ring[] = [];
      for (let i = 0; i <= N; i++) {
        const u = i / N;
        const fork = t === 'forked' && u > 0.5 ? 1 + (u - 0.5) * 1.2 : 1;
        rs.push({ c: add(top, [0, -L * u, 0.012 * u - (t === 'waist' ? 0.03 * u * u : 0)]), rs: W * (1 - 0.65 * u) * fork + 0.002, rf: W * 0.5 * (1 - 0.6 * u) + 0.002, skin: sk(u), lw: 1 });
      }
      loft(s, rs, { sides: 5, front: [0, 0, 1], mat: ctx.headMat, region: REG.extra, capStart: 'flat', capEnd: 'point', capEndLen: 0.008, jitter: 0.08, seed: ctx.seed + 11, tangent: () => [0, -1, 0.15] });
    });
  }
  if (mu !== 'none' && !spec.flags.mustacheHidden) {
    part(ctx, 'head.mustache', (s) => {
      const c = info.seam(0);
      for (const side of [1, -1]) {
        const p0: V3 = [0, ctx.mouthY! + 0.008, c[2] + 0.004];
        const L = mu === 'floor' ? 0.4 : mu === 'walrus' ? 0.05 : 0.06;
        const end: V3 = mu === 'handlebar' ? [side * 0.055, ctx.mouthY! + 0.02, c[2] - 0.01] : mu === 'horseshoe' || mu === 'walrus' || mu === 'floor'
          ? [side * 0.035, ctx.mouthY! - L, c[2] - 0.004] : [side * L, ctx.mouthY! + 0.004, c[2] - 0.012];
        const mid = scl(add(p0, end), 0.5);
        const pts = resample([p0, add(mid, [side * 0.01, 0.006, 0.002]), end], 4);
        strip(s, pts, [0.006, 0.006, 0.004, 0.001], [0, 1, 0], () => rigid(ctx.bi('mouth')), { mat: MAT.ink, region: REG.extra, lw: 0.8 });
      }
    });
  }
}

/** Builds every head part. Returns nothing; fills ctx.mb. */
export function buildHead(ctx: BuildCtx): void {
  skull(ctx);
  muzzle(ctx);
  mouth(ctx);
  nose(ctx);
  eyes(ctx);
  brows(ctx);
  teeth(ctx);
  whiskers(ctx);
  ears(ctx);
  horns(ctx);
  hair(ctx);
  beard(ctx);
}
